import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit3,
  Loader2,
  TicketPercent,
  Save,
  X,
  Power,
  Copy,
} from "lucide-react";
import { toast } from "sonner";

import { deliveryApi } from "../services/deliveryApi";
import DeliveryNav from "../components/delivery/DeliveryNav";

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const formatDesconto = (c) =>
  c.tipo === "percentual" ? `${c.valor}% OFF` : `${formatBRL(c.valor)} OFF`;

export default function DeliveryCupons() {
  const { negocioId } = useParams();
  const [loja, setLoja] = useState(null);
  const [cupons, setCupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const l = await deliveryApi.getLojaByNegocio(negocioId);
      setLoja(l);
      const list = await deliveryApi.listCupons(l.id);
      setCupons(list);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Ative o Delivery primeiro");
      } else {
        toast.error("Erro ao carregar cupons");
      }
    } finally {
      setLoading(false);
    }
  }, [negocioId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (data) => {
    try {
      const payload = {
        codigo: data.codigo.trim().toUpperCase(),
        descricao: data.descricao || null,
        tipo: data.tipo,
        valor: parseFloat(data.valor),
        minimo_pedido: parseFloat(data.minimo_pedido || 0),
        limite_uso_total: data.limite_uso_total ? parseInt(data.limite_uso_total, 10) : null,
        limite_por_cliente: parseInt(data.limite_por_cliente || 1, 10),
        valido_ate: data.valido_ate || null,
        ativo: !!data.ativo,
      };
      if (editing?.id) {
        // Não permite trocar código no update
        const { codigo: _ignored, ...without } = payload;
        await deliveryApi.updateCupom(editing.id, without);
        toast.success("Cupom atualizado");
      } else {
        await deliveryApi.createCupom({ loja_id: loja.id, ...payload });
        toast.success("Cupom criado");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar cupom");
    }
  };

  const handleDelete = async (c) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remover cupom "${c.codigo}"?`)) return;
    try {
      await deliveryApi.deleteCupom(c.id);
      toast.success("Cupom removido");
      await load();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  const handleToggle = async (c) => {
    try {
      await deliveryApi.updateCupom(c.id, { ativo: !c.ativo });
      await load();
    } catch {
      toast.error("Falha ao alterar status");
    }
  };

  const copiar = async (codigo) => {
    try {
      await navigator.clipboard.writeText(codigo);
      toast.success("Código copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (!loja) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Link to={`/empreendedor/delivery/${negocioId}`} className="text-[var(--primary)] underline">
          Voltar ao painel
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] pb-12">
      <DeliveryNav negocioId={negocioId} negocioNome={loja.nome_loja} />
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <TicketPercent className="w-5 h-5 text-orange-500" /> Cupons de desconto
            </h1>
            <p className="text-xs text-[var(--text-secondary)]">
              Crie códigos promocionais para clientes do delivery
            </p>
          </div>
          <button
            onClick={() =>
              setEditing({
                codigo: "",
                descricao: "",
                tipo: "percentual",
                valor: 10,
                minimo_pedido: 0,
                limite_uso_total: "",
                limite_por_cliente: 1,
                valido_ate: "",
                ativo: true,
              })
            }
            className="px-3 py-2 text-sm bg-[var(--primary)] text-white rounded-lg inline-flex items-center gap-2"
            data-testid="cupom-new"
          >
            <Plus className="w-4 h-4" /> Novo cupom
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {cupons.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-[var(--border)] p-8 text-center">
            <TicketPercent className="w-10 h-10 text-[var(--text-secondary)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-secondary)]">
              Nenhum cupom criado ainda. Cupons aumentam o ticket médio e fidelizam clientes.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cupons.map((c) => (
              <div
                key={c.id}
                className="bg-white rounded-2xl border border-[var(--border)] p-4 flex gap-3"
                data-testid={`cupom-${c.id}`}
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-white flex flex-col items-center justify-center shrink-0">
                  <TicketPercent className="w-5 h-5" />
                  <span className="text-[10px] font-bold mt-0.5">
                    {c.tipo === "percentual" ? `${c.valor}%` : "FIXO"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="font-mono font-bold text-base">{c.codigo}</code>
                    <button
                      onClick={() => copiar(c.codigo)}
                      className="p-1 hover:bg-[var(--bg-secondary)] rounded"
                      title="Copiar código"
                      data-testid={`cupom-copy-${c.id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                    {!c.ativo && (
                      <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded">
                        inativo
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-[var(--primary)]">
                    {formatDesconto(c)}
                  </p>
                  {c.descricao && (
                    <p className="text-xs text-[var(--text-secondary)] truncate">{c.descricao}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-1 text-[10px] text-[var(--text-secondary)]">
                    {c.minimo_pedido > 0 && (
                      <span>min {formatBRL(c.minimo_pedido)}</span>
                    )}
                    {c.limite_uso_total && (
                      <span>
                        {c.usos_count || 0}/{c.limite_uso_total} usos
                      </span>
                    )}
                    {c.valido_ate && <span>até {c.valido_ate.slice(0, 10)}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleToggle(c)}
                    className={`p-1.5 rounded ${
                      c.ativo
                        ? "text-emerald-700 hover:bg-emerald-50"
                        : "text-red-700 hover:bg-red-50"
                    }`}
                    title={c.ativo ? "Desativar" : "Ativar"}
                    data-testid={`cupom-toggle-${c.id}`}
                  >
                    <Power className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() =>
                      setEditing({
                        id: c.id,
                        codigo: c.codigo,
                        descricao: c.descricao || "",
                        tipo: c.tipo,
                        valor: c.valor,
                        minimo_pedido: c.minimo_pedido || 0,
                        limite_uso_total: c.limite_uso_total || "",
                        limite_por_cliente: c.limite_por_cliente || 1,
                        valido_ate: c.valido_ate ? c.valido_ate.slice(0, 10) : "",
                        ativo: c.ativo,
                      })
                    }
                    className="p-1.5 hover:bg-[var(--bg-secondary)] rounded"
                    data-testid={`cupom-edit-${c.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(c)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    data-testid={`cupom-delete-${c.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <CupomFormModal
          initial={editing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CupomFormModal({ initial, onSave, onClose }) {
  const [data, setData] = useState(initial);
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4"
      data-testid="cupom-modal"
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)] sticky top-0 bg-white">
          <h3 className="font-semibold">
            {initial.id ? "Editar cupom" : "Novo cupom"}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-secondary)] rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {!initial.id && (
            <div>
              <label className="block text-xs font-medium mb-1">Código *</label>
              <input
                type="text"
                value={data.codigo}
                onChange={(e) =>
                  setData({ ...data, codigo: e.target.value.toUpperCase().replace(/\s/g, "") })
                }
                placeholder="BEMVINDO20"
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm font-mono uppercase"
                data-testid="cupom-codigo"
              />
              <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                Letras e números, sem espaços. Não é possível alterar depois de criar.
              </p>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium mb-1">Descrição (opcional)</label>
            <input
              type="text"
              value={data.descricao}
              onChange={(e) => setData({ ...data, descricao: e.target.value })}
              placeholder="Bem-vindo! 20% no primeiro pedido"
              className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
              data-testid="cupom-descricao"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Tipo *</label>
              <select
                value={data.tipo}
                onChange={(e) => setData({ ...data, tipo: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="cupom-tipo"
              >
                <option value="percentual">% Percentual</option>
                <option value="fixo">R$ Fixo</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                {data.tipo === "percentual" ? "% Desconto *" : "R$ Desconto *"}
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={data.valor}
                onChange={(e) => setData({ ...data, valor: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="cupom-valor"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Pedido mínimo</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={data.minimo_pedido}
                onChange={(e) => setData({ ...data, minimo_pedido: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="cupom-minimo"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Limite por cliente</label>
              <input
                type="number"
                min="1"
                value={data.limite_por_cliente}
                onChange={(e) => setData({ ...data, limite_por_cliente: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="cupom-limite-cliente"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Limite total (opcional)</label>
              <input
                type="number"
                min="1"
                value={data.limite_uso_total}
                onChange={(e) => setData({ ...data, limite_uso_total: e.target.value })}
                placeholder="Ilimitado"
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="cupom-limite-total"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Válido até</label>
              <input
                type="date"
                value={data.valido_ate}
                onChange={(e) => setData({ ...data, valido_ate: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm"
                data-testid="cupom-valido-ate"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!data.ativo}
              onChange={(e) => setData({ ...data, ativo: e.target.checked })}
              data-testid="cupom-ativo"
            />
            Cupom ativo
          </label>
        </div>
        <div className="p-4 border-t border-[var(--border)] flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm border border-[var(--border)] rounded-lg"
            data-testid="cupom-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(data)}
            disabled={!data.codigo || !data.valor}
            className="flex-1 px-4 py-2 text-sm bg-[var(--primary)] text-white rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="cupom-save"
          >
            <Save className="w-4 h-4" /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
